import React, { useCallback, useMemo, useState } from 'react';
import { Pin } from 'components/forms';
import PropTypes from 'prop-types';
import {
  InfoAlert,
  Heading_1 as Heading1,
  PrimaryButton,
  SecondaryButton,
  WarningAlert,
  ErrorAlert,
} from '@huggg/react-components';
import styles from './prepaidCard.module.scss';
import BackButton from '../BackButton/BackButton';
import { format, isValid, parseISO } from 'date-fns';
import SvgIcon from '../SvgIcon/SvgIcon';
import { fallbackError } from 'utilities/errorHandling';

const Detail = ({ label, value, link }) => (
  <div className={styles.personalDetailBlock}>
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span className={styles.personalDetailLabel}>{label}</span>
      {link}
    </div>
    {value.map((v, i) => (
      <div className={styles.personalDetail} key={i}>
        {v}
      </div>
    ))}
  </div>
);

const getKnownProviderErrors = (shortcode) => ({
  'Forbidden - Invalid OTP': {
    declineCardRequest: false,
    displayMessage:
      'Your One Time Passcode is invalid. Please double check the One Time Passcode sent to your email address and try again',
  },
  'Forbidden - ExceededProgramAnnualLimit': {
    declineCardRequest: true,
    displayMessage:
      'Your request for a card has been declined, as our system detects that the amount you have received in last 12 months exceeds the program limit. Please contact the card sender for more information.',
  },
  'Forbidden - ExceededActiveAccountLimit': {
    declineCardRequest: true,
    displayMessage:
      'Your request for a card has been declined, as our system detects that you have already received the maximum number of cards allowed on this program. Please contact the card sender for more information.',
  },
  'Forbidden - TrustLevel': {
    declineCardRequest: true,
    displayMessage: `We were unable to create your account. Please contact support@huggg.me, quoting this error code - ERR-3065-${shortcode ?? 'no-shortcode'}`,
  },
});

export const PrepaidFormConfirmation = (props) => {
  const {
    formData,
    onSubmit,
    onBack,
    onResendOtp,
    otpDelay,
    voucherError,
    shortcode,
  } = props;
  const {
    firstName,
    lastName,
    phoneNumber,
    dateOfBirth,
    postalAddress,
    email,
  } = formData;
  const { addressLine1, addressLine2, city, postcode, country } = postalAddress;

  const formatDateOfBirth = useCallback((dateOfBirth) => {
    const dobString = `${dateOfBirth.year}-${dateOfBirth.month}-${dateOfBirth.date}`;
    const formattedDoB = isValid(parseISO(dobString))
      ? format(parseISO(dobString), 'do MMM yyyy')
      : 'Invalid Date';
    return formattedDoB;
  }, []);

  const [showOtpResend, setShowOtpResend] = useState(false);

  const toggleOtpResend = () => {
    setShowOtpResend(!showOtpResend);
  };

  const knownProviderErrors = useMemo(
    () => getKnownProviderErrors(shortcode),
    [shortcode],
  );
  const providerErrorMessage = useMemo(
    () => voucherError?.metadata?.providerError?.message,
    [voucherError],
  );
  const shouldDeclineRequest = useMemo(
    () =>
      providerErrorMessage &&
      knownProviderErrors[providerErrorMessage]?.declineCardRequest,
    [providerErrorMessage, knownProviderErrors],
  );

  const getError = () => {
    if (shouldDeclineRequest) {
      return (
        <WarningAlert
          title="Card request declined"
          message={knownProviderErrors[providerErrorMessage]?.displayMessage}
        />
      );
    } else {
      return (
        <ErrorAlert
          message={
            knownProviderErrors[providerErrorMessage]?.displayMessage ??
            providerErrorMessage ??
            fallbackError()
          }
        />
      );
    }
  };

  return (
    <div>
      {voucherError && (
        <div style={{ margin: '16px 16px 0' }}>{getError()}</div>
      )}
      {!shouldDeclineRequest && (
        <Heading1 className={styles.heavyMargin}>
          Confirm your personal details
        </Heading1>
      )}
      <div className={styles.confirmationBlock}>
        <div className={styles.heavyMargin}>
          <Detail label={'Full Name'} value={[`${firstName} ${lastName}`]} />
          <Detail label={'Phone number'} value={[phoneNumber]} />
          <Detail
            label={'Date of birth'}
            value={[formatDateOfBirth(dateOfBirth)]}
          />
          <Detail
            label={'Postal Address'}
            value={[addressLine1, addressLine2, city, postcode, country]}
          />
          <Detail
            label={'Email address'}
            value={[email]}
            link={
              !shouldDeclineRequest && (
                <button className={styles.buttonAsLink} onClick={onBack}>
                  Change
                </button>
              )
            }
          />
        </div>

        {!shouldDeclineRequest && (
          <div className={styles.otpBlock}>
            <InfoAlert
              message={
                'Please enter the One Time Passcode that has been sent to your email address.'
              }
            />

            <button className={styles.buttonAsLink} onClick={toggleOtpResend}>
              I didn&apos;t recieve my One Time Passcode
              <SvgIcon
                name={showOtpResend ? 'chevron-up' : 'chevron-down'}
                className={styles.chevronIcon}
              />
            </button>

            {!!showOtpResend && (
              <>
                <SecondaryButton
                  disabled={otpDelay > 0}
                  style={{ border: '1px solid black' }}
                  arrow="none"
                  label={`RESEND ONE TIME PASSCODE ${
                    otpDelay > 0 ? `IN ${otpDelay}s` : ''
                  }`}
                  onClick={onResendOtp}
                />
                <div className={styles.otpReminder}>
                  Please check that you have entered the correct email address
                  and don’t forget to check your spam/junk folders too.
                </div>
              </>
            )}
            <Pin
              className={styles.pinInput}
              pinLength={6}
              requiredValidationError="Please enter a valid, 6-digit One Time Passcode"
              onSubmit={onSubmit}
              button={<PrimaryButton label="CLAIM MY CARD" />}
            />
          </div>
        )}
        {!shouldDeclineRequest && <BackButton onClick={onBack} />}
      </div>
    </div>
  );
};

PrepaidFormConfirmation.propTypes = {
  formData: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onResendOtp: PropTypes.func.isRequired,
  voucherError: PropTypes.object,
  shortcode: PropTypes.string,
};
